/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { useContext, useMemo, useEffect, Children, isValidElement, cloneElement, useRef, useState, createContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes, ThemeContext } from 'styled-components';
import { math, rgba, readableColor } from 'polished';
import { retrieveComponentStyles, DEFAULT_THEME, getLineHeight, getColor, PALETTE, SELECTOR_FOCUS_VISIBLE, focusStyles, useDocument } from '@zendeskgarden/react-theming';
import { useAccordion } from '@zendeskgarden/container-accordion';
import { getControlledValue } from '@zendeskgarden/container-utilities';
import { useUIDSeed } from 'react-uid';
import mergeRefs from 'react-merge-refs';
import activeElement from 'dom-helpers/activeElement';

function _extends$3() {
  _extends$3 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$3.apply(this, arguments);
}

const ChromeContext = React__default.createContext({
  hue: 'chromeHue'
});
const useChromeContext = () => {
  return useContext(ChromeContext);
};

const COMPONENT_ID$A = 'chrome.chrome';
const StyledChrome = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$A,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledChrome",
  componentId: "sc-1uqm6u6-0"
})(["display:flex;position:relative;margin:0;height:100vh;overflow-y:hidden;font-family:", ";direction:", ";", ";"], props => props.theme.fonts.system, props => props.theme.rtl && 'rtl', props => retrieveComponentStyles(COMPONENT_ID$A, props));
StyledChrome.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$z = 'chrome.header_item_icon';
const StyledHeaderItemIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$z,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledHeaderItemIcon",
  componentId: "sc-1jhhp6z-0"
})(["transition:transform 0.25s ease-in-out;margin:0 3px;width:", ";min-width:", ";height:", ";", ";"], props => props.theme.iconSizes.md, props => props.theme.iconSizes.md, props => props.theme.iconSizes.md, props => retrieveComponentStyles(COMPONENT_ID$z, props));
StyledHeaderItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$y = 'chrome.base_header_item';
const getHeaderItemSize = props => `${props.theme.space.base * 7.5}px`;
const sizeStyles$4 = props => {
  const size = props.theme.space.base * 7.5;
  return css(["padding:0 3px;min-width:", "px;height:", ";line-height:", ";"], size, props.maxY ? '100%' : `${size}px`, getLineHeight(size, props.theme.fontSizes.md));
};
const StyledBaseHeaderItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$y,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledBaseHeaderItem",
  componentId: "sc-1qua7h6-0"
})(["display:inline-flex;position:relative;flex:", ";align-items:center;justify-content:", ";order:1;transition:box-shadow 0.1s ease-in-out,color 0.1s ease-in-out;z-index:0;margin:", ";border:none;border-radius:", ";background:transparent;text-decoration:none;white-space:nowrap;color:inherit;font-size:inherit;", " ", ";"], props => props.maxX && '1', props => props.maxX ? 'start' : 'center', props => `0 ${props.theme.space.base * 3}px`, props => {
  if (props.isRound) {
    return '100%';
  }
  if (props.maxY) {
    return '0';
  }
  return props.theme.borderRadii.md;
}, sizeStyles$4, props => retrieveComponentStyles(COMPONENT_ID$y, props));
StyledBaseHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$x = 'chrome.header_item_text';
const clippedStyling = css(["position:absolute;margin:0;clip:rect(1px,1px,1px,1px);width:1px;height:1px;overflow:hidden;white-space:nowrap;"]);
const StyledHeaderItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$x,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledHeaderItemText",
  componentId: "sc-goz7la-0"
})(["margin:0 3px;", " ", ";"], props => props.isClipped && clippedStyling, props => retrieveComponentStyles(COMPONENT_ID$x, props));
StyledHeaderItemText.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$w = 'chrome.nav';
const colorStyles$6 = props => {
  const shade = props.isDark || props.isLight ? 600 : 700;
  const backgroundColor = getColor(props.hue, shade, props.theme);
  const foregroundColor = props.isLight ? props.theme.palette.black : props.theme.palette.white;
  return css(["background-color:", ";color:", ";"], backgroundColor, foregroundColor);
};
const getNavWidth = props => {
  return `${props.theme.space.base * 15}px`;
};
const getExpandedNavWidth = () => {
  return `200px`;
};
const StyledNav = styled.nav.attrs({
  'data-garden-id': COMPONENT_ID$w,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledNav",
  componentId: "sc-6j462r-0"
})(["display:flex;position:relative;flex-direction:column;flex-shrink:0;order:-1;width:", ";font-size:", ";", ";", ";"], props => props.isExpanded ? getExpandedNavWidth : getNavWidth, props => props.theme.fontSizes.md, props => colorStyles$6(props), props => retrieveComponentStyles(COMPONENT_ID$w, props));
StyledNav.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$v = 'chrome.header_item';
const retrieveProductColor$1 = props => {
  switch (props.product) {
    case 'chat':
      return PALETTE.product.chat;
    case 'connect':
      return PALETTE.product.connect;
    case 'explore':
      return PALETTE.product.explore;
    case 'guide':
      return PALETTE.product.guide;
    case 'message':
      return PALETTE.product.message;
    case 'support':
      return PALETTE.product.support;
    case 'talk':
      return PALETTE.product.talk;
    default:
      return 'inherit';
  }
};
const StyledLogoHeaderItem = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$v,
  'data-garden-version': '8.74.0',
  as: 'div'
}).withConfig({
  displayName: "StyledLogoHeaderItem",
  componentId: "sc-1n1d1yv-0"
})(["display:none;order:0;margin-right:", ";margin-left:", ";border-", ":", ";border-radius:0;padding:0;width:", ";height:100%;overflow:hidden;fill:", ";text-decoration:none;color:", ";", "{", "}", "{margin:0;width:", ";height:", ";}", ";"], props => props.theme.rtl ? `-${props.theme.space.base}px` : 'auto', props => props.theme.rtl ? 'auto' : `-${props.theme.space.base}px`, props => props.theme.rtl ? 'left' : 'right', props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}`, props => getNavWidth(props), props => getColor('chromeHue', 700, props.theme), props => retrieveProductColor$1(props), StyledHeaderItemText, clippedStyling, StyledHeaderItemIcon, props => props.theme.iconSizes.lg, props => props.theme.iconSizes.lg, props => retrieveComponentStyles(COMPONENT_ID$v, props));
StyledLogoHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$u = 'chrome.base_nav_item';
const getNavItemHeight = props => {
  return `${props.theme.space.base * 13}px`;
};
const sizeStyles$3 = props => {
  const verticalPadding = math(`(${getNavItemHeight(props)} - ${props.theme.iconSizes.lg}) / 2`);
  const horizontalPadding = math(`(${getNavWidth(props)} - ${props.theme.iconSizes.lg}) / 4`);
  return css(["padding:", " ", ";min-height:", ";"], verticalPadding, horizontalPadding, getNavItemHeight);
};
const StyledBaseNavItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$u,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledBaseNavItem",
  componentId: "sc-zvo43f-0"
})(["display:flex;flex-shrink:0;align-items:center;justify-content:center;transition:outline-color 0.1s ease-in-out,box-shadow 0.1s ease-in-out,background-color 0.1s ease-in-out,opacity 0.1s ease-in-out;border:none;box-sizing:border-box;background:transparent;text-decoration:none;color:inherit;font-size:inherit;", ""], props => sizeStyles$3(props));
StyledBaseNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$t = 'chrome.header';
const getHeaderHeight = props => {
  return getNavItemHeight(props);
};
const StyledHeader = styled.header.attrs({
  'data-garden-id': COMPONENT_ID$t,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledHeader",
  componentId: "sc-1cexpz-0"
})(["display:flex;position:", ";align-items:center;justify-content:flex-end;box-sizing:border-box;border-bottom:", ";box-shadow:", ";background-color:", ";padding:0 ", "px;height:", ";color:", ";font-size:", ";", " ", ";"], props => props.isStandalone && 'relative', props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}`, props => props.isStandalone && props.theme.shadows.lg('0', '10px', getColor('chromeHue', 600, props.theme, 0.15)), props => props.theme.colors.background, props => props.theme.space.base, getHeaderHeight, props => getColor('neutralHue', 600, props.theme), props => props.theme.fontSizes.md, props => props.isStandalone && `
    ${StyledLogoHeaderItem} {
      display: inline-flex;
    }
  `, props => retrieveComponentStyles(COMPONENT_ID$t, props));
StyledHeader.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$s = 'chrome.skipnav';
const animationStyles = () => {
  const animationName = keyframes(["0%{transform:translate(-50%,-50%);}"]);
  return css(["transition:opacity 0.2s ease-out,clip 0s linear 0.2s;opacity:0;clip:rect(0,0,0,0);&:focus{transition:opacity 0.2s ease-in-out;animation:0.2s cubic-bezier(0.15,0.85,0.35,1.2) ", ";opacity:1;clip:rect(0,150vw,100vh,-50vw);}"], animationName);
};
const colorStyles$5 = theme => {
  const color = getColor('primaryHue', 600, theme);
  const borderColor = getColor('neutralHue', 300, theme);
  const boxShadow = theme.shadows.lg(`${theme.space.base * 5}px`, `${theme.space.base * 7}px`, getColor('chromeHue', 600, theme, 0.15));
  return css(["border-color:", ";box-shadow:", ";background-color:", ";color:", ";&:hover,&:focus{color:", ";}", ""], borderColor, boxShadow, theme.colors.background, color, color, focusStyles({
    theme,
    inset: true,
    boxShadow
  }));
};
const sizeStyles$2 = props => {
  const top = math(`${getHeaderHeight(props)} / 2`);
  const padding = `${props.theme.space.base * 5}px`;
  const paddingStart = `${props.theme.space.base * 4}px`;
  const fontSize = props.theme.fontSizes.md;
  const lineHeight = getLineHeight(padding, fontSize);
  return css(["top:", ";padding:", ";padding-", ":", ";line-height:", ";font-size:", ";"], top, padding, props.theme.rtl ? 'right' : 'left', paddingStart, lineHeight, fontSize);
};
const StyledSkipNav = styled.a.attrs({
  'data-garden-id': COMPONENT_ID$s,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSkipNav",
  componentId: "sc-1tsro34-0"
})(["", ";display:inline-flex;position:absolute;left:50%;align-items:center;justify-content:center;transform:translateX(-50%);direction:", ";z-index:", ";border:", ";border-radius:", ";text-decoration:underline;white-space:nowrap;", ";", "{text-decoration:none;}&:hover{text-decoration:underline;}", ";", ";"], animationStyles(), props => props.theme.rtl && 'rtl', props => props.zIndex, props => props.theme.borders.sm, props => props.theme.borderRadii.md, props => sizeStyles$2(props), SELECTOR_FOCUS_VISIBLE, props => colorStyles$5(props.theme), props => retrieveComponentStyles(COMPONENT_ID$s, props));
StyledSkipNav.defaultProps = {
  theme: DEFAULT_THEME
};

var _path$2;
function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var SvgLinkStroke = function SvgLinkStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    focusable: "false",
    viewBox: "0 0 16 16",
    "aria-hidden": "true"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
  })));
};

const COMPONENT_ID$r = 'chrome.skipnav_icon';
const sizeStyles$1 = theme => {
  const margin = `${theme.space.base * 2}px`;
  const size = theme.iconSizes.md;
  return css(["margin-", ":", ";width:", ";height:", ";"], theme.rtl ? 'left' : 'right', margin, size, size);
};
const StyledSkipNavIcon = styled(SvgLinkStroke).attrs({
  'data-garden-id': COMPONENT_ID$r,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSkipNavIcon",
  componentId: "sc-1ika5z4-0"
})(["transform:", ";color:", ";", ";", ";"], props => props.theme.rtl && 'scaleX(-1)', props => getColor('neutralHue', 600, props.theme), props => sizeStyles$1(props.theme), props => retrieveComponentStyles(COMPONENT_ID$r, props));
StyledSkipNavIcon.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$q = 'chrome.body';
const StyledBody = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$q,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledBody",
  componentId: "sc-c7h9kv-0"
})(["flex:1;order:1;background-color:", ";min-width:0;", ";"], props => getColor('neutralHue', 100, props.theme), props => retrieveComponentStyles(COMPONENT_ID$q, props));
StyledBody.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$p = 'chrome.footer';
const getFooterHeight = props => {
  return `${props.theme.space.base * 20}px`;
};
const StyledFooter = styled.footer.attrs({
  'data-garden-id': COMPONENT_ID$p,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledFooter",
  componentId: "sc-v7lib2-0"
})(["display:flex;align-items:center;justify-content:flex-end;box-sizing:border-box;border-top:", ";background-color:", ";padding:0 ", "px;height:", ";", ";"], props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}`, props => props.theme.colors.background, props => props.theme.space.base * 9, getFooterHeight, props => retrieveComponentStyles(COMPONENT_ID$p, props));
StyledFooter.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$o = 'chrome.content';
const StyledContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$o,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledContent",
  componentId: "sc-qcuzxn-0"
})(["display:flex;height:", ";line-height:", ";color:", ";font-size:", ";&:focus{outline:none;}", ";"], props => props.hasFooter ? `calc(100% - ${math(`${getHeaderHeight(props)} + ${getFooterHeight(props)}`)})` : `calc(100% - ${getHeaderHeight(props)})`, props => getLineHeight(props.theme.lineHeights.md, props.theme.fontSizes.md), props => props.theme.colors.foreground, props => props.theme.fontSizes.md, props => retrieveComponentStyles(COMPONENT_ID$o, props));
StyledContent.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$n = 'chrome.main';
const StyledMain = styled.main.attrs({
  'data-garden-id': COMPONENT_ID$n,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledMain",
  componentId: "sc-t61cre-0"
})(["flex:1;order:1;background-color:", ";overflow:auto;:focus{outline:none;}", ";"], props => props.theme.colors.background, props => retrieveComponentStyles(COMPONENT_ID$n, props));
StyledMain.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$m = 'chrome.sidebar';
const StyledSidebar = styled.aside.attrs({
  'data-garden-id': COMPONENT_ID$m,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSidebar",
  componentId: "sc-1q77fuw-0"
})(["flex-shrink:0;order:0;box-sizing:border-box;border-", ":", ";width:330px;overflow:auto;&:focus{outline:none;}", ";"], props => props.theme.rtl ? 'left' : 'right', props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}`, props => retrieveComponentStyles(COMPONENT_ID$m, props));
StyledSidebar.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$l = 'chrome.footer_item';
const StyledFooterItem = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$l,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledFooterItem",
  componentId: "sc-1cktm85-0"
})(["margin:", ";", ";"], props => `0 ${props.theme.space.base}px`, props => retrieveComponentStyles(COMPONENT_ID$l, props));
StyledFooterItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$k = 'chrome.header_item';
const imgStyles = props => {
  const size = math(`${getHeaderItemSize(props)} - ${props.theme.space.base * 2}`);
  return css(["img{margin:0;border-radius:", ";width:", ";height:", ";}"], math(`${props.theme.borderRadii.md} - 1`), size, size);
};
const StyledHeaderItem = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$k,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledHeaderItem",
  componentId: "sc-14sft6n-0"
})(["&:hover,&:focus{text-decoration:none;color:inherit;}", " &:focus-visible:active,&[data-garden-focus-visible]:active{box-shadow:none;}&:hover ", ",&:hover ", ",&:active ", ",&:active ", "{color:", ";}", " ", " ", ";"], props => focusStyles({
  theme: props.theme,
  inset: props.maxY
}), StyledHeaderItemIcon, StyledHeaderItemText, StyledHeaderItemIcon, StyledHeaderItemText, props => getColor('chromeHue', 700, props.theme), imgStyles, props => props.isRound && `
    ${StyledHeaderItemIcon} {
      border-radius: 100px;
    }
  `, props => retrieveComponentStyles(COMPONENT_ID$k, props));
StyledHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$j = 'chrome.header_item_wrapper';
const StyledHeaderItemWrapper = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$j,
  'data-garden-version': '8.74.0',
  as: 'div'
}).withConfig({
  displayName: "StyledHeaderItemWrapper",
  componentId: "sc-1uieu55-0"
})(["", ";"], props => retrieveComponentStyles(COMPONENT_ID$j, props));
StyledHeaderItemWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$i = 'chrome.logo_nav_item';
const retrieveProductColor = product => {
  switch (product) {
    case 'chat':
      return PALETTE.product.chat;
    case 'connect':
      return PALETTE.product.connect;
    case 'explore':
      return PALETTE.product.explore;
    case 'guide':
      return PALETTE.product.guide;
    case 'message':
      return PALETTE.product.message;
    case 'support':
      return PALETTE.product.support;
    case 'talk':
      return PALETTE.product.talk;
    default:
      return 'inherit';
  }
};
const colorStyles$4 = props => {
  const fillColor = props.isLight ? props.theme.palette.grey[800] : props.theme.palette.white;
  const color = props.isLight || props.isDark ? fillColor : retrieveProductColor(props.product);
  return css(["color:", ";fill:", ";"], color, fillColor);
};
const StyledLogoNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$i,
  'data-garden-version': '8.74.0',
  as: 'div'
}).withConfig({
  displayName: "StyledLogoNavItem",
  componentId: "sc-saaydx-0"
})(["order:0;opacity:1;cursor:default;", ";"], props => colorStyles$4(props));
StyledLogoNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$h = 'chrome.brandmark_nav_item';
const StyledBrandmarkNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$h,
  'data-garden-version': '8.74.0',
  as: 'div'
}).withConfig({
  displayName: "StyledBrandmarkNavItem",
  componentId: "sc-8kynd4-0"
})(["order:1;opacity:0.3;margin-top:auto;"]);
StyledBrandmarkNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$g = 'chrome.nav_item_icon';
const StyledNavItemIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$g,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledNavItemIcon",
  componentId: "sc-7w9rpt-0"
})(["align-self:flex-start;order:0;border-radius:", ";width:", ";height:", ";", ";"], props => props.theme.borderRadii.md, props => props.theme.iconSizes.lg, props => props.theme.iconSizes.lg, props => retrieveComponentStyles(COMPONENT_ID$g, props));
StyledNavItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$f = 'chrome.nav_item';
const colorStyles$3 = props => {
  const {
    theme,
    hue,
    isLight,
    isDark,
    isCurrent
  } = props;
  const DARK = theme.palette.black;
  const LIGHT = theme.palette.white;
  let currentColor;
  let hoverColor;
  if (isCurrent) {
    if (isLight) {
      currentColor = rgba(DARK, 0.4);
    } else if (isDark) {
      currentColor = rgba(LIGHT, 0.4);
    } else {
      currentColor = getColor(hue, 500, theme);
    }
  } else {
    hoverColor = rgba(isLight ? LIGHT : DARK, 0.1);
  }
  const activeColor = rgba(isLight ? DARK : LIGHT, 0.1);
  const focusColor = isLight ? DARK : LIGHT;
  return css(["opacity:", ";outline-color:transparent;background-color:", ";&:hover{opacity:1;background-color:", ";}", " &:active{background-color:", ";}"], isCurrent ? 1 : 0.6, currentColor, hoverColor, focusStyles({
    theme,
    condition: false ,
    styles: {
      opacity: 1,
      outlineColor: focusColor
    }
  }), activeColor);
};
const StyledNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$f,
  'data-garden-version': '8.74.0',
  as: 'button'
}).withConfig({
  displayName: "StyledNavItem",
  componentId: "sc-gs8mjz-0"
})(["justify-content:", ";order:1;margin:0;cursor:", ";text-align:", ";&:hover,&:focus{text-decoration:none;color:inherit;}", ";&:focus-visible:hover,&:focus-visible:active,", "{outline:", " solid;outline-offset:-", ";}", " ", ";"], props => props.isExpanded && 'start', props => props.isCurrent ? 'default' : 'pointer', props => props.isExpanded && 'inherit', colorStyles$3, SELECTOR_FOCUS_VISIBLE, props => math(`${props.theme.borderWidths.md} - 1`), props => props.theme.borderWidths.md, props => props.isExpanded && `
    ${StyledNavItemIcon} {
      margin: 0 ${math(`(${getNavWidth(props)} - ${props.theme.iconSizes.lg}) / 4`)};
    }
  `, props => retrieveComponentStyles(COMPONENT_ID$f, props));
StyledNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$e = 'chrome.nav_item_text';
const StyledNavItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$e,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledNavItemText",
  componentId: "sc-13m84xl-0"
})(["position:absolute;order:1;clip:rect(1px,1px,1px,1px);margin:", ";width:1px;height:1px;overflow:hidden;line-height:", ";white-space:", ";", " ", ";"], props => props.isExpanded && `0 ${math(`(${getNavWidth(props)} - ${props.theme.iconSizes.lg}) / 4`)}`, props => getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md), props => props.isWrapped ? 'normal' : 'nowrap', props => props.isExpanded && `
    ${StyledNavItem} > && {
      position: static;
      flex: 1;
      clip: auto;
      width: auto;
      height: auto;
      text-overflow: ellipsis;
    }
  `, props => retrieveComponentStyles(COMPONENT_ID$e, props));
StyledNavItemText.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$d = 'chrome.subnav_item';
const colorStyles$2 = props => {
  const {
    theme,
    isLight,
    isCurrent
  } = props;
  const DARK = theme.palette.black;
  const LIGHT = theme.palette.white;
  let currentColor;
  let hoverColor;
  if (isCurrent) {
    if (isLight) {
      currentColor = rgba(DARK, 0.1);
    } else {
      currentColor = rgba(LIGHT, 0.1);
    }
  } else {
    hoverColor = rgba(isLight ? LIGHT : DARK, 0.1);
  }
  const activeColor = rgba(isLight ? DARK : LIGHT, 0.03);
  return css(["opacity:", ";background-color:", ";&:hover{opacity:1;background-color:", ";}", " &:not([data-garden-header='true']):active{background-color:", ";}"], isCurrent ? '1' : '0.6', currentColor, hoverColor, focusStyles({
    theme,
    hue: isLight ? DARK : LIGHT,
    spacerWidth: null,
    styles: {
      opacity: 1
    }
  }), activeColor);
};
const getSubNavItemHeight = props => {
  return `${props.theme.space.base * 7.5}px`;
};
const StyledSubNavItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$d,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSubNavItem",
  componentId: "sc-1yg9dpx-0"
})(["display:flex;align-items:center;transition:box-shadow 0.1s ease-in-out,background-color 0.1s ease-in-out,opacity 0.1s ease-in-out;margin:", "px 0 0;border:none;border-radius:", ";box-sizing:border-box;background:transparent;cursor:", ";padding:", ";width:100%;min-height:", ";text-align:inherit;font-size:inherit;&,&:hover,&:focus{text-decoration:none;color:inherit;}", ";", ";"], props => props.theme.space.base * 2, props => props.theme.borderRadii.md, props => props.isCurrent ? 'default' : 'pointer', props => `0 ${props.theme.space.base * 2}px`, getSubNavItemHeight, props => colorStyles$2(props), props => retrieveComponentStyles(COMPONENT_ID$d, props));
StyledSubNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$c = 'chrome.subnav';
const colorStyles$1 = props => {
  let shade;
  if (props.isLight) {
    shade = 500;
  } else {
    shade = props.isDark ? 700 : 800;
  }
  const backgroundColor = getColor(props.hue, shade, props.theme);
  const foregroundColor = props.isLight ? props.theme.palette.grey[800] : props.theme.palette.white;
  return css(["background-color:", ";color:", ";"], backgroundColor, foregroundColor);
};
const StyledSubNav = styled.nav.attrs({
  'data-garden-id': COMPONENT_ID$c,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSubNav",
  componentId: "sc-19hjou6-0"
})(["flex-direction:column;order:0;padding:", ";min-width:220px;overflow:auto;font-size:", ";", ";& > ", ":first-child{margin-top:0;}", ";"], props => `${props.theme.space.base * 6}px ${props.theme.space.base * 5}px`, props => props.theme.fontSizes.md, props => colorStyles$1(props), StyledSubNavItem, props => retrieveComponentStyles('chrome.subnav', props));
StyledSubNav.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$b = 'chrome.subnav_item_text';
const sizeStyles = props => {
  const baseLineHeight = props.theme.space.base * 5;
  const verticalMargin = math(`(${getSubNavItemHeight(props)} - ${baseLineHeight}) / 2`);
  const lineHeight = getLineHeight(baseLineHeight, props.theme.fontSizes.md);
  return css(["margin:", " 0;line-height:", ";"], verticalMargin, lineHeight);
};
const StyledSubNavItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$b,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSubNavItemText",
  componentId: "sc-1hy0pn7-0"
})(["overflow:hidden;text-overflow:ellipsis;white-space:", ";", " ", ";"], props => props.isWrapped ? 'normal' : 'nowrap', props => sizeStyles(props), props => retrieveComponentStyles(COMPONENT_ID$b, props));
StyledSubNavItemText.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$a = 'chrome.collapsible_sub_nav_item';
const StyledSubNavItemHeader = styled(StyledSubNavItem).attrs({
  'data-garden-id': COMPONENT_ID$a,
  'data-garden-version': '8.74.0',
  'data-garden-header': 'true'
}).withConfig({
  displayName: "StyledSubNavItemHeader",
  componentId: "sc-1vniter-0"
})(["position:relative;padding-", ":", "px;", ";"], props => props.theme.rtl ? 'left' : 'right', props => props.theme.space.base * 7, props => retrieveComponentStyles(COMPONENT_ID$a, props));
StyledSubNavItemHeader.defaultProps = {
  theme: DEFAULT_THEME
};

var _path$1;
function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
var SvgChevronDownStroke = function SvgChevronDownStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    focusable: "false",
    viewBox: "0 0 12 12",
    "aria-hidden": "true"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M1.646 3.646a.5.5 0 01.638-.057l.07.057L6 7.293l3.646-3.647a.5.5 0 01.638-.057l.07.057a.5.5 0 01.057.638l-.057.07-4 4a.5.5 0 01-.638.057l-.07-.057-4-4a.5.5 0 010-.708z"
  })));
};

const COMPONENT_ID$9 = 'chrome.collapsible_sub_nav_item_icon';
const FilteredChevronDownStrokeIcon = React__default.forwardRef((_ref, ref) => {
  let {
    theme,
    isExpanded,
    ...validProps
  } = _ref;
  return React__default.createElement(SvgChevronDownStroke, _extends$3({
    ref: ref
  }, validProps));
});
FilteredChevronDownStrokeIcon.displayName = 'FilteredChevronDownStrokeIcon';
const StyledSubNavItemIcon = styled(FilteredChevronDownStrokeIcon).withConfig({
  displayName: "StyledSubNavItemIcon",
  componentId: "sc-1d02hho-0"
})(["width:", ";height:", ";"], props => props.theme.iconSizes.sm, props => props.theme.iconSizes.sm);
StyledSubNavItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};
const StyledSubNavItemIconWrapper = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$9,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSubNavItemIcon__StyledSubNavItemIconWrapper",
  componentId: "sc-1d02hho-1"
})(["display:flex;position:absolute;top:0;right:", ";left:", ";align-items:center;justify-content:center;width:", "px;height:", ";", "{transform:", ";transition:transform 0.25s ease-in-out;}", ";"], props => props.theme.rtl ? 'auto' : 0, props => props.theme.rtl && 0, props => props.theme.space.base * 7, getSubNavItemHeight, StyledSubNavItemIcon, props => props.isExpanded && `rotate(${props.theme.rtl ? '-' : ''}180deg)`, props => retrieveComponentStyles(COMPONENT_ID$9, props));
StyledSubNavItemIconWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

const PANEL_COMPONENT_ID = 'chrome.collapsible_sub_nav_item_panel';
const hiddenStyling = css(["visibility:hidden;max-height:0 !important;overflow:hidden;"]);
const StyledSubNavPanel = styled.div.attrs({
  'data-garden-id': PANEL_COMPONENT_ID,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSubNavPanel",
  componentId: "sc-1jv3rpv-0"
})(["transition:max-height 0.25s cubic-bezier(0.15,0.85,0.35,1.2),0.25s visibility 0s linear;height:auto;max-height:100%;", " ", "{padding-", ":", ";}", ";"], props => props.isHidden && hiddenStyling, StyledSubNavItem, props => props.theme.rtl ? 'right' : 'left', props => `${props.theme.space.base * 5}px`, props => retrieveComponentStyles(PANEL_COMPONENT_ID, props));
StyledSubNavPanel.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$8 = 'chrome.sheet';
const borderStyle = _ref => {
  let {
    theme,
    placement,
    isOpen
  } = _ref;
  const borderColor = isOpen ? getColor('neutralHue', 300, theme) : 'transparent';
  const borderSides = ['-left', '-right'];
  let borderSide = '';
  if (theme.rtl) {
    borderSides.reverse();
  }
  if (placement === 'end') {
    borderSide = borderSides[0];
  } else if (placement === 'start') {
    borderSide = borderSides[1];
  }
  return `border${borderSide}: ${theme.borders.sm} ${borderColor};`;
};
const StyledSheet = styled.aside.attrs({
  'data-garden-id': COMPONENT_ID$8,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheet",
  componentId: "sc-dx8ijk-0"
})(["display:flex;order:1;transition:", ";background-color:", ";width:", ";height:100%;overflow:hidden;font-size:", ";&:focus{outline:none;}", ";", ";"], props => props.isAnimated && 'width 250ms ease-in-out', props => props.theme.colors.background, props => props.isOpen ? props.size : '0px', props => props.theme.fontSizes.md, props => borderStyle(props), props => retrieveComponentStyles(COMPONENT_ID$8, props));
StyledSheet.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$7 = 'chrome.sheet_wrapper';
const StyledSheetWrapper = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$7,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetWrapper",
  componentId: "sc-f6x9zb-0"
})(["display:flex;position:relative;flex-direction:column;transform:", ";transition:", ";min-width:", ";", ";"], props => {
  const translateValues = [-100, 100];
  let translation = 'translateX(0%)';
  if (props.isOpen) {
    return translation;
  }
  if (props.theme.rtl) {
    translateValues.reverse();
  }
  if (props.placement === 'end') {
    translation = `translateX(${translateValues[1]}%)`;
  } else if (props.placement === 'start') {
    translation = `translateX(${translateValues[0]}%)`;
  }
  return translation;
}, props => props.isAnimated && 'transform 250ms ease-in-out', props => props.size, props => retrieveComponentStyles(COMPONENT_ID$7, props));
StyledSheetWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$6 = 'chrome.sheet_title';
const StyledSheetTitle = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$6,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetTitle",
  componentId: "sc-1gogk75-0"
})(["line-height:", ";color:", ";font-weight:", ";", ";"], props => getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md), props => props.theme.colors.foreground, props => props.theme.fontWeights.semibold, props => retrieveComponentStyles(COMPONENT_ID$6, props));
StyledSheetTitle.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$5 = 'chrome.sheet_description';
const StyledSheetDescription = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetDescription",
  componentId: "sc-1puglb6-0"
})(["line-height:", ";color:", ";", ";"], props => getLineHeight(props.theme.space.base * 4, props.theme.fontSizes.md), props => getColor('neutralHue', 600, props.theme), props => retrieveComponentStyles(COMPONENT_ID$5, props));
StyledSheetDescription.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$4 = 'chrome.sheet_body';
const StyledSheetBody = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$4,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetBody",
  componentId: "sc-bt4eoj-0"
})(["flex:1;overflow-y:auto;padding:", "px;", ";"], props => props.theme.space.base * 5, props => retrieveComponentStyles(COMPONENT_ID$4, props));
StyledSheetBody.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$3 = 'chrome.sheet_close';
const BASE_MULTIPLIERS = {
  top: 2.5,
  side: 2,
  size: 10
};
const colorStyles = props => {
  const backgroundColor = 'primaryHue';
  const foregroundColor = 'neutralHue';
  return css(["background-color:transparent;color:", ";&:hover{background-color:", ";color:", ";}", " &:active{transition:background-color 0.1s ease-in-out,color 0.1s ease-in-out;background-color:", ";color:", ";}"], getColor(foregroundColor, 600, props.theme), getColor(backgroundColor, 600, props.theme, 0.08), getColor(foregroundColor, 700, props.theme), focusStyles({
    theme: props.theme
  }), getColor(backgroundColor, 600, props.theme, 0.2), getColor(foregroundColor, 800, props.theme));
};
const StyledSheetClose = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetClose",
  componentId: "sc-1ab02oq-0"
})(["display:flex;position:absolute;top:", "px;", ":", ";align-items:center;justify-content:center;transition:box-shadow 0.1s ease-in-out,background-color 0.25s ease-in-out,color 0.25s ease-in-out;border:none;border-radius:50%;cursor:pointer;padding:0;width:", "px;height:", "px;overflow:hidden;text-decoration:none;font-size:0;user-select:none;&::-moz-focus-inner{border:0;}", ";& > svg{vertical-align:middle;}", ";"], props => props.theme.space.base * BASE_MULTIPLIERS.top, props => props.theme.rtl ? 'left' : 'right', props => `${props.theme.space.base * BASE_MULTIPLIERS.side}px`, props => props.theme.space.base * BASE_MULTIPLIERS.size, props => props.theme.space.base * BASE_MULTIPLIERS.size, props => colorStyles(props), props => retrieveComponentStyles(COMPONENT_ID$3, props));
StyledSheetClose.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$2 = 'chrome.sheet_footer';
const StyledSheetFooter = styled.footer.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetFooter",
  componentId: "sc-2cktos-0"
})(["display:flex;flex-flow:row wrap;align-items:center;justify-content:", ";border-top:", ";padding:", "px;", ";"], props => props.isCompact ? 'center' : 'flex-end', props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}}`, props => props.theme.space.base * (props.isCompact ? 2.5 : 5), props => retrieveComponentStyles(COMPONENT_ID$2, props));
StyledSheetFooter.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID$1 = 'chrome.sheet_footer_item';
const StyledSheetFooterItem = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetFooterItem",
  componentId: "sc-r9ixh-0"
})(["", " ", ";"], props => `margin-${props.theme.rtl ? 'right' : 'left'}: ${props.theme.space.base * 5}px;`, props => retrieveComponentStyles(COMPONENT_ID$1, props));
StyledSheetFooterItem.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID = 'chrome.sheet_header';
const StyledSheetHeader = styled.header.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledSheetHeader",
  componentId: "sc-o2ry8i-0"
})(["border-bottom:", ";padding:", "px;", "  ", ";"], props => `${props.theme.borders.sm} ${getColor('neutralHue', 300, props.theme)}}`, props => props.theme.space.base * 5, props => props.isCloseButtonPresent && `padding-${props.theme.rtl ? 'left' : 'right'}: ${props.theme.space.base * (BASE_MULTIPLIERS.size + BASE_MULTIPLIERS.side + 2)}px;`, props => retrieveComponentStyles(COMPONENT_ID, props));
StyledSheetHeader.defaultProps = {
  theme: DEFAULT_THEME
};

const Chrome = React__default.forwardRef((_ref, ref) => {
  let {
    hue,
    isFluid,
    ...props
  } = _ref;
  const theme = useContext(ThemeContext);
  const isLightMemoized = useMemo(() => {
    if (hue) {
      const backgroundColor = getColor(hue, 600, theme);
      const LIGHT_COLOR = 'white';
      return readableColor(backgroundColor, LIGHT_COLOR, undefined, false ) === LIGHT_COLOR;
    }
    return false;
  }, [hue, theme]);
  const isLight = hue ? isLightMemoized : false;
  const isDark = hue ? !isLightMemoized : false;
  const chromeContextValue = useMemo(() => ({
    hue: hue || 'chromeHue',
    isLight,
    isDark
  }), [hue, isLight, isDark]);
  const environment = useDocument(theme);
  useEffect(() => {
    if (environment && !isFluid) {
      const htmlElement = environment.querySelector('html');
      if (htmlElement) {
        const defaultHtmlPosition = htmlElement.style.position;
        htmlElement.style.position = 'fixed';
        return () => {
          htmlElement.style.position = defaultHtmlPosition;
        };
      }
    }
    return undefined;
  }, [environment, isFluid]);
  return React__default.createElement(ChromeContext.Provider, {
    value: chromeContextValue
  }, React__default.createElement(StyledChrome, _extends$3({
    ref: ref
  }, props)));
});
Chrome.displayName = 'Chrome';
Chrome.propTypes = {
  hue: PropTypes.string
};

const SkipNav = React__default.forwardRef((_ref, ref) => {
  let {
    targetId,
    zIndex,
    children,
    ...props
  } = _ref;
  return React__default.createElement(StyledSkipNav, _extends$3({
    href: `#${targetId}`,
    zIndex: zIndex,
    ref: ref
  }, props), React__default.createElement(StyledSkipNavIcon, null), children);
});
SkipNav.displayName = 'SkipNav';
SkipNav.propTypes = {
  targetId: PropTypes.string.isRequired,
  zIndex: PropTypes.number
};
SkipNav.defaultProps = {
  zIndex: 1
};

const BodyContext = React__default.createContext({
  hasFooter: true
});
const useBodyContext = () => {
  return useContext(BodyContext);
};

const Body$1 = React__default.forwardRef((_ref, ref) => {
  let {
    hasFooter,
    ...props
  } = _ref;
  const bodyContextValue = useMemo(() => ({
    hasFooter: !!hasFooter
  }), [hasFooter]);
  return React__default.createElement(BodyContext.Provider, {
    value: bodyContextValue
  }, React__default.createElement(StyledBody, _extends$3({
    ref: ref
  }, props)));
});
Body$1.displayName = 'Body';
Body$1.propTypes = {
  hasFooter: PropTypes.bool
};

const Content = React__default.forwardRef((props, ref) => {
  const {
    hasFooter
  } = useBodyContext();
  return React__default.createElement(StyledContent, _extends$3({
    ref: ref,
    hasFooter: hasFooter
  }, props));
});
Content.displayName = 'Content';

const Main = React__default.forwardRef((props, ref) => React__default.createElement(StyledMain, _extends$3({
  ref: ref
}, props)));
Main.displayName = 'Main';

const Sidebar = React__default.forwardRef((props, ref) => React__default.createElement(StyledSidebar, _extends$3({
  ref: ref
}, props)));
Sidebar.displayName = 'Sidebar';

const Header$1 = React__default.forwardRef((props, ref) => React__default.createElement(StyledHeader, _extends$3({
  ref: ref
}, props)));
Header$1.displayName = 'Header';
Header$1.propTypes = {
  isStandalone: PropTypes.bool
};

const PLACEMENT = ['end', 'start'];
const PRODUCT = ['chat', 'connect', 'explore', 'guide', 'message', 'support', 'talk'];

const HeaderItem = React__default.forwardRef((_ref, ref) => {
  let {
    hasLogo,
    product,
    ...other
  } = _ref;
  if (hasLogo) {
    return React__default.createElement(StyledLogoHeaderItem, _extends$3({
      ref: ref,
      product: product
    }, other));
  }
  return React__default.createElement(StyledHeaderItem, _extends$3({
    ref: ref
  }, other));
});
HeaderItem.displayName = 'HeaderItem';
HeaderItem.propTypes = {
  maxX: PropTypes.bool,
  maxY: PropTypes.bool,
  isRound: PropTypes.bool,
  product: PropTypes.oneOf(PRODUCT),
  hasLogo: PropTypes.bool
};

const HeaderItemIcon = _ref => {
  let {
    children,
    ...props
  } = _ref;
  const element = Children.only(children);
  if ( isValidElement(element)) {
    const Icon = _ref2 => {
      let {
        theme,
        ...iconProps
      } = _ref2;
      return cloneElement(element, {
        ...props,
        ...iconProps
      });
    };
    return React__default.createElement(StyledHeaderItemIcon, _extends$3({
      as: Icon
    }, props));
  }
  return null;
};

const HeaderItemText = React__default.forwardRef((props, ref) => React__default.createElement(StyledHeaderItemText, _extends$3({
  ref: ref
}, props)));
HeaderItemText.displayName = 'HeaderItemText';
HeaderItemText.propTypes = {
  isClipped: PropTypes.bool
};

const HeaderItemWrapper = React__default.forwardRef((props, ref) => React__default.createElement(StyledHeaderItemWrapper, _extends$3({
  ref: ref
}, props)));
HeaderItemWrapper.displayName = 'HeaderItemWrapper';

const Footer$1 = React__default.forwardRef((props, ref) => React__default.createElement(StyledFooter, _extends$3({
  ref: ref
}, props)));
Footer$1.displayName = 'Footer';

const FooterItem$1 = React__default.forwardRef((props, ref) => React__default.createElement(StyledFooterItem, _extends$3({
  ref: ref
}, props)));
FooterItem$1.displayName = 'FooterItem';

const NavContext = React__default.createContext({
  isExpanded: false
});
const useNavContext = () => {
  return useContext(NavContext);
};

const Nav = React__default.forwardRef((props, ref) => {
  const {
    hue,
    isLight,
    isDark
  } = useChromeContext();
  const navContextValue = useMemo(() => ({
    isExpanded: !!props.isExpanded
  }), [props.isExpanded]);
  return React__default.createElement(NavContext.Provider, {
    value: navContextValue
  }, React__default.createElement(StyledNav, _extends$3({
    ref: ref
  }, props, {
    hue: hue,
    isLight: isLight,
    isDark: isDark
  })));
});
Nav.displayName = 'Nav';
Nav.propTypes = {
  isExpanded: PropTypes.bool
};

const NavItem = React__default.forwardRef((_ref, ref) => {
  let {
    hasLogo,
    hasBrandmark,
    product,
    ...other
  } = _ref;
  const {
    hue,
    isLight,
    isDark
  } = useChromeContext();
  const {
    isExpanded
  } = useNavContext();
  const ariaCurrent = other.isCurrent || undefined;
  if (hasLogo) {
    return React__default.createElement(StyledLogoNavItem, _extends$3({
      ref: ref,
      isDark: isDark,
      isLight: isLight,
      product: product,
      "aria-current": ariaCurrent
    }, other));
  }
  if (hasBrandmark) {
    return React__default.createElement(StyledBrandmarkNavItem, _extends$3({
      ref: ref
    }, other));
  }
  return React__default.createElement(StyledNavItem, _extends$3({
    tabIndex: 0,
    ref: ref,
    isExpanded: isExpanded,
    hue: hue,
    isDark: isDark,
    isLight: isLight,
    "aria-current": ariaCurrent
  }, other));
});
NavItem.displayName = 'NavItem';
NavItem.propTypes = {
  product: PropTypes.oneOf(PRODUCT),
  hasLogo: PropTypes.bool,
  hasBrandmark: PropTypes.bool
};

const NavItemIcon = _ref => {
  let {
    children,
    ...props
  } = _ref;
  const element = Children.only(children);
  if ( isValidElement(element)) {
    const Icon = _ref2 => {
      let {
        theme,
        ...iconProps
      } = _ref2;
      return cloneElement(element, {
        ...props,
        ...iconProps
      });
    };
    return React__default.createElement(StyledNavItemIcon, _extends$3({
      as: Icon
    }, props));
  }
  return null;
};

const NavItemText = React__default.forwardRef((props, ref) => {
  const {
    isExpanded
  } = useNavContext();
  return React__default.createElement(StyledNavItemText, _extends$3({
    ref: ref,
    isExpanded: isExpanded
  }, props));
});
NavItemText.displayName = 'NavItemText';
NavItemText.propTypes = {
  isWrapped: PropTypes.bool
};

const SubNav = React__default.forwardRef((props, ref) => {
  const {
    hue,
    isLight,
    isDark
  } = useChromeContext();
  return React__default.createElement(StyledSubNav, _extends$3({
    ref: ref,
    hue: hue,
    isLight: isLight,
    isDark: isDark
  }, props));
});
SubNav.displayName = 'SubNav';

const SubNavItem = React__default.forwardRef((props, ref) => {
  const {
    isDark,
    isLight
  } = useChromeContext();
  return React__default.createElement(StyledSubNavItem, _extends$3({
    ref: ref,
    isDark: isDark,
    isLight: isLight
  }, props));
});
SubNavItem.displayName = 'SubNavItem';
SubNavItem.propTypes = {
  isCurrent: PropTypes.bool
};

const SubNavItemText = React__default.forwardRef((props, ref) => React__default.createElement(StyledSubNavItemText, _extends$3({
  ref: ref
}, props)));
SubNavItemText.displayName = 'SubNavItemText';
SubNavItemText.propTypes = {
  isWrapped: PropTypes.bool
};

const CollapsibleSubNavItem = React__default.forwardRef((_ref, ref) => {
  let {
    header,
    children,
    isExpanded: controlledExpanded,
    onChange,
    ...other
  } = _ref;
  const {
    isDark,
    isLight
  } = useChromeContext();
  const panelRef = useRef();
  const [internalExpanded, setInternalExpanded] = useState(controlledExpanded);
  const expanded = getControlledValue(controlledExpanded, internalExpanded);
  const expandedSections = expanded ? [0] : [];
  const {
    getHeaderProps,
    getTriggerProps,
    getPanelProps
  } = useAccordion({
    expandedSections,
    onChange: () => {
      const isExpanded = expandedSections.length === 0;
      if (onChange) {
        onChange(isExpanded);
      } else {
        setInternalExpanded(isExpanded);
      }
    }
  });
  useEffect(() => {
    if (expanded && panelRef.current) {
      panelRef.current.style.maxHeight = `${panelRef.current.scrollHeight}px`;
    }
  }, [expanded, children]);
  return React__default.createElement("div", {
    ref: ref
  }, React__default.createElement("div", getHeaderProps({
    ariaLevel: 2
  }), React__default.createElement(StyledSubNavItemHeader, _extends$3({
    isDark: isDark,
    isLight: isLight
  }, getTriggerProps({
    isExpanded: expanded,
    index: 0,
    role: null,
    tabIndex: null,
    ...other
  })), React__default.createElement(React__default.Fragment, null, header, React__default.createElement(StyledSubNavItemIconWrapper, {
    isExpanded: expanded
  }, React__default.createElement(StyledSubNavItemIcon, null))))), React__default.createElement(StyledSubNavPanel, getPanelProps({
    index: 0,
    isHidden: !expanded,
    ref: panelRef
  }), children));
});
CollapsibleSubNavItem.propTypes = {
  header: PropTypes.any,
  isExpanded: PropTypes.bool,
  onChange: PropTypes.func
};
CollapsibleSubNavItem.displayName = 'CollapsibleSubNavItem';

const SheetContext = createContext({
  setIsCloseButtonPresent() {}
});
const useSheetContext = () => {
  return useContext(SheetContext);
};

function useFocusableMount(_ref) {
  let {
    isMounted,
    focusOnMount,
    restoreFocus,
    targetRef
  } = _ref;
  const triggerRef = useRef();
  useEffect(() => {
    if (isMounted && focusOnMount && targetRef.current) {
      triggerRef.current = activeElement();
      targetRef.current.focus();
    }
  }, [isMounted, focusOnMount, targetRef]);
  useEffect(() => {
    if (!isMounted && restoreFocus && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [isMounted, restoreFocus, triggerRef]);
}

const SheetTitle = forwardRef((_ref, ref) => {
  let {
    id,
    ...props
  } = _ref;
  const {
    titleId
  } = useSheetContext();
  return React__default.createElement(StyledSheetTitle, _extends$3({
    id: id || titleId,
    ref: ref
  }, props));
});
SheetTitle.displayName = 'Sheet.Title';
const Title = SheetTitle;

const SheetDescription = forwardRef((_ref, ref) => {
  let {
    id,
    ...props
  } = _ref;
  const {
    descriptionId
  } = useSheetContext();
  return React__default.createElement(StyledSheetDescription, _extends$3({
    id: id || descriptionId,
    ref: ref
  }, props));
});
SheetDescription.displayName = 'Sheet.Description';
const Description = SheetDescription;

const SheetHeader = forwardRef((props, ref) => {
  const {
    isCloseButtonPresent
  } = useSheetContext();
  return React__default.createElement(StyledSheetHeader, _extends$3({
    ref: ref,
    isCloseButtonPresent: isCloseButtonPresent
  }, props));
});
SheetHeader.displayName = 'Sheet.Header';
const Header = SheetHeader;

const SheetBody = forwardRef((props, ref) => {
  return React__default.createElement(StyledSheetBody, _extends$3({
    ref: ref
  }, props));
});
SheetBody.displayName = 'Sheet.Body';
const Body = SheetBody;

const SheetFooter = forwardRef((props, ref) => {
  return React__default.createElement(StyledSheetFooter, _extends$3({
    ref: ref
  }, props));
});
SheetFooter.displayName = 'Sheet.Footer';
const Footer = SheetFooter;

const SheetFooterItem = forwardRef((props, ref) => {
  return React__default.createElement(StyledSheetFooterItem, _extends$3({
    ref: ref
  }, props));
});
SheetFooterItem.displayName = 'Sheet.FooterItem';
const FooterItem = SheetFooterItem;

var _path;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgXStroke = function SvgXStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    focusable: "false",
    viewBox: "0 0 16 16",
    "aria-hidden": "true"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M3 13L13 3m0 10L3 3"
  })));
};

const SheetClose = forwardRef((props, ref) => {
  const {
    setIsCloseButtonPresent
  } = useSheetContext();
  useEffect(() => {
    setIsCloseButtonPresent(true);
    return () => setIsCloseButtonPresent(false);
  }, [setIsCloseButtonPresent]);
  return React__default.createElement(StyledSheetClose, _extends$3({
    "aria-label": "Close Sheet",
    ref: ref
  }, props), React__default.createElement(SvgXStroke, null));
});
SheetClose.displayName = 'Sheet.Close';
const Close = SheetClose;

const SheetComponent = React__default.forwardRef((_ref, ref) => {
  let {
    id,
    isOpen,
    isAnimated,
    focusOnMount,
    restoreFocus,
    placement,
    size,
    children,
    ...props
  } = _ref;
  const sheetRef = useRef(null);
  const seed = useUIDSeed();
  const [isCloseButtonPresent, setIsCloseButtonPresent] = useState(false);
  const idPrefix = useMemo(() => id || seed(`sheet_${'8.74.0'}`), [id, seed]);
  const titleId = `${idPrefix}--title`;
  const descriptionId = `${idPrefix}--description`;
  const sheetContext = useMemo(() => ({
    titleId,
    descriptionId,
    isCloseButtonPresent,
    setIsCloseButtonPresent
  }), [titleId, descriptionId, isCloseButtonPresent]);
  useFocusableMount({
    targetRef: sheetRef,
    isMounted: isOpen,
    focusOnMount,
    restoreFocus
  });
  return React__default.createElement(SheetContext.Provider, {
    value: sheetContext
  }, React__default.createElement(StyledSheet, _extends$3({
    isOpen: isOpen,
    isAnimated: isAnimated,
    placement: placement,
    size: size,
    tabIndex: -1,
    id: idPrefix,
    "aria-labelledby": titleId,
    "aria-describedby": descriptionId,
    ref: mergeRefs([sheetRef, ref])
  }, props), React__default.createElement(StyledSheetWrapper, {
    isOpen: isOpen,
    isAnimated: isAnimated,
    placement: placement,
    size: size
  }, children)));
});
SheetComponent.displayName = 'Sheet';
SheetComponent.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  isAnimated: PropTypes.bool,
  focusOnMount: PropTypes.bool,
  restoreFocus: PropTypes.bool,
  placement: PropTypes.oneOf(PLACEMENT),
  size: PropTypes.string
};
SheetComponent.defaultProps = {
  isAnimated: true,
  placement: 'end',
  size: '380px'
};
const Sheet = SheetComponent;
Sheet.Body = Body;
Sheet.Close = Close;
Sheet.Description = Description;
Sheet.Footer = Footer;
Sheet.FooterItem = FooterItem;
Sheet.Header = Header;
Sheet.Title = Title;

export { Body$1 as Body, Chrome, CollapsibleSubNavItem, Content, Footer$1 as Footer, FooterItem$1 as FooterItem, Header$1 as Header, HeaderItem, HeaderItemIcon, HeaderItemText, HeaderItemWrapper, Main, Nav, NavItem, NavItemIcon, NavItemText, PRODUCT as PRODUCTS, Sheet, Sidebar, SkipNav, SubNav, SubNavItem, SubNavItemText };
